import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAddMemberBundleMutation, useRemoveMemberBundleMutation } from '@features/organizations';
import { useUserId } from '@features/user';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';

import { useEventTracking } from '../../../services/statsig';
import {
  useCreateSetupIntentMutation,
  useGetFullSubscriptionDetailsQuery,
  useGetPaymentMethodQuery,
  useGetUserBundlesQuery,
  useUpdateBillingIntervalMutation,
  useUpdatePaymentMethodMutation,
} from '../store/api';
import type { BillingInterval, BundleWithStatus } from '../types';

import { PaymentMethodCard } from './PaymentMethodCard';
import { confirmPaymentMethod, PaymentMethodSection } from './PaymentMethodSection';
import { PlanSummary } from './PlanSummary';
import { UpdatePaymentMethodModal } from './UpdatePaymentMethodModal';

interface ConfirmPaymentStepProps {
  selectedTier: BundleWithStatus | null;
  selectedAddOns: BundleWithStatus[];
  billingInterval: BillingInterval;
  onBillingIntervalChange: (interval: BillingInterval) => void;
  onChangeTier?: () => void;
  onFlowCompleted?: () => void;
}

type CheckoutState =
  | 'checking_payment' // Checking if payment method exists
  | 'ready' // Ready to activate
  | 'collecting_payment' // User filling out payment form (waiting for Stripe confirmation)
  | 'processing_payment' // Confirming payment with Stripe
  | 'processing_bundles' // Assigning bundles to user
  | 'success' // Complete
  | 'error'; // Error occurred

export const ConfirmPaymentStep: React.FC<ConfirmPaymentStepProps> = ({
  selectedTier,
  selectedAddOns,
  billingInterval,
  onBillingIntervalChange,
  onChangeTier,
  onFlowCompleted,
}) => {
  const { subscription } = useEventTracking();

  const [checkoutState, setCheckoutState] = useState<CheckoutState>('checking_payment');
  const [error, setError] = useState<string | null>(null);
  const [setupIntentSecret, setSetupIntentSecret] = useState<string | null>(null);
  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);
  const [isPaymentElementLoaded, setIsPaymentElementLoaded] = useState(false);
  const [isPaymentFormReady, setIsPaymentFormReady] = useState(false);
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const paymentStartTime = useRef<number>(Date.now());
  const hasTrackedViewRef = useRef(false);
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // API hooks
  const userId = useUserId();
  const [addMemberBundle] = useAddMemberBundleMutation();
  const [removeMemberBundle] = useRemoveMemberBundleMutation();
  const [createSetupIntent] = useCreateSetupIntentMutation();
  const [updatePaymentMethod] = useUpdatePaymentMethodMutation();
  const [updateBillingInterval] = useUpdateBillingIntervalMutation();

  const { data: subscriptionDetails, refetch: refetchSubscriptionDetails } =
    useGetFullSubscriptionDetailsQuery();
  const { data: existingPaymentMethod, isLoading: isCheckingPayment } = useGetPaymentMethodQuery();
  const { data: userBundlesData } = useGetUserBundlesQuery();
  const existingBundles = userBundlesData?.bundles ?? [];

  // Extract billing details from subscription
  const billingInfo = subscriptionDetails?.billing_details;

  // Determine if payment method is needed
  const needsPaymentMethod = !existingPaymentMethod?.payment_method && !paymentMethodId;

  // Calculate total amount
  const calculateTotalAmount = useCallback(() => {
    let total = 0;
    if (selectedTier) {
      total +=
        billingInterval === 'month'
          ? selectedTier.monthly_unit_amount
          : selectedTier.yearly_unit_amount;
    }
    selectedAddOns.forEach(addOn => {
      total += billingInterval === 'month' ? addOn.monthly_unit_amount : addOn.yearly_unit_amount;
    });
    return total;
  }, [selectedTier, selectedAddOns, billingInterval]);

  // Track payment step viewed
  useEffect(() => {
    if (checkoutState !== 'checking_payment' && !hasTrackedViewRef.current) {
      hasTrackedViewRef.current = true;
      subscription.paymentStepViewed({
        tier_id: selectedTier?.bundle_id,
        addon_count: selectedAddOns.length,
        billing_interval: billingInterval,
        total_amount: calculateTotalAmount(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutState]);

  // Check payment method and create SetupIntent if needed
  useEffect(() => {
    const initializePayment = async () => {
      if (checkoutState !== 'checking_payment') return;

      // Wait for payment method check to complete
      if (isCheckingPayment) return;

      // If payment method exists, user can proceed directly
      if (existingPaymentMethod?.payment_method) {
        if (isMountedRef.current) {
          setCheckoutState('ready');
        }
        return;
      }

      // No payment method - create SetupIntent for collection
      try {
        const result = await createSetupIntent().unwrap();
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setSetupIntentSecret(result.client_secret);
          setCheckoutState('collecting_payment');
        }
      } catch (err: any) {
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setError(err?.data?.message || 'Failed to initialize payment. Please try again.');
          setCheckoutState('error');
        }
      }
    };

    initializePayment();
  }, [checkoutState, existingPaymentMethod, isCheckingPayment, createSetupIntent]);

  // Assign bundles to the current user
  const assignBundlesToUser = useCallback(
    async (skipSuccessHandling = false) => {
      if (!userId) {
        setError('User ID not found. Please try logging in again.');
        setCheckoutState('error');

        // Track payment failure
        subscription.paymentFailed({
          error_message: 'User ID not found',
          tier_id: selectedTier?.bundle_id,
          addon_count: selectedAddOns.length,
          total_amount: calculateTotalAmount(),
        });
        return;
      }

      setError(null);

      try {
        // Build set of existing bundle IDs for quick lookup
        const existingBundleIds = new Set(existingBundles.map(b => b.bundle_id));

        // Build set of selected bundle IDs (tier + add-ons)
        const selectedBundleIds = new Set<string>();
        if (selectedTier) selectedBundleIds.add(selectedTier.bundle_id);
        selectedAddOns.forEach(a => selectedBundleIds.add(a.bundle_id));

        // Calculate bundles to add (selected but not existing)
        const bundlesToAdd: BundleWithStatus[] = [];
        if (selectedTier && !existingBundleIds.has(selectedTier.bundle_id)) {
          bundlesToAdd.push(selectedTier);
        }
        selectedAddOns.forEach(addOn => {
          if (!existingBundleIds.has(addOn.bundle_id)) {
            bundlesToAdd.push(addOn);
          }
        });

        // Calculate bundles to remove (existing but not selected). Only
        // member-level bundles (tier/add_on) are reconciled here; any other
        // assignment type an org may still hold is left untouched so member
        // checkout never revokes an org-level entitlement.
        const bundlesToRemove = existingBundles.filter(
          b =>
            (b.bundle_type === 'tier' || b.bundle_type === 'add_on') &&
            !selectedBundleIds.has(b.bundle_id),
        );

        // Execute adds (tier + add_on are member-level)
        for (const bundle of bundlesToAdd) {
          await addMemberBundle({
            userId,
            adminAddBundleRequest: {
              bundle_id: bundle.bundle_id,
            },
          }).unwrap();
        }

        // Execute removes
        for (const bundle of bundlesToRemove) {
          await removeMemberBundle({
            userId,
            assignmentId: bundle.assignment_id,
          }).unwrap();
        }

        // Handle success state and navigation if not skipped
        // (when skipSuccessHandling is true, billing interval is set by the caller)
        if (!skipSuccessHandling) {
          // Set billing interval before completing
          console.log('[ConfirmPaymentStep] Setting billing interval to:', billingInterval);
          await updateBillingInterval({
            changeBillingIntervalRequest: { billing_interval: billingInterval },
          }).unwrap();
          // Success!
          const paymentDuration = Math.round((Date.now() - paymentStartTime.current) / 1000);

          // Track payment succeeded
          subscription.paymentSucceeded({
            tier_id: selectedTier?.bundle_id,
            tier_name: selectedTier?.name,
            addon_count: selectedAddOns.length,
            addon_ids: selectedAddOns.map(addon => addon.bundle_id),
            addon_names: selectedAddOns.map(addon => addon.name),
            billing_interval: billingInterval,
            total_amount: calculateTotalAmount(),
            payment_duration: paymentDuration,
          });

          // Track subscription created
          subscription.subscriptionCreated({
            tier_id: selectedTier?.bundle_id,
            tier_name: selectedTier?.name,
            addon_count: selectedAddOns.length,
            addon_ids: selectedAddOns.map(addon => addon.bundle_id),
            addon_names: selectedAddOns.map(addon => addon.name),
            billing_interval: billingInterval,
            total_amount: calculateTotalAmount(),
          });

          // Track flow completed
          onFlowCompleted?.();

          setCheckoutState('success');

          // Refetch subscription details to get updated bundles
          await refetchSubscriptionDetails();
        }
      } catch (err: any) {
        const errorMessage =
          err?.data?.message || 'Failed to activate subscription. Please try again.';
        setError(errorMessage);
        setCheckoutState('error');

        // Track payment failure
        subscription.paymentFailed({
          error_message: errorMessage,
          tier_id: selectedTier?.bundle_id,
          addon_count: selectedAddOns.length,
          total_amount: calculateTotalAmount(),
        });
        throw err; // Re-throw so caller can handle
      }
    },
    [
      userId,
      selectedTier,
      selectedAddOns,
      existingBundles,
      billingInterval,
      calculateTotalAmount,
      addMemberBundle,
      removeMemberBundle,
      updateBillingInterval,
      subscription,
      refetchSubscriptionDetails,
      onFlowCompleted,
    ],
  );

  // Handle payment method confirmed from PaymentMethodSection
  const handlePaymentMethodConfirmed = useCallback(
    async (confirmedPaymentMethodId: string) => {
      console.log('[ConfirmPaymentStep] Payment method confirmed:', confirmedPaymentMethodId);
      setPaymentMethodId(confirmedPaymentMethodId);
      // Transition to processing state immediately to avoid showing broken UI
      setCheckoutState('processing_bundles');

      // Track payment details entered
      subscription.paymentDetailsEntered({
        has_payment_method: true,
      });

      try {
        // First: Assign bundles (stages the subscription)
        console.log('[ConfirmPaymentStep] Assigning bundles to user');
        await assignBundlesToUser(true); // Skip success handling, we'll handle it after payment

        // Second: Set billing interval
        console.log('[ConfirmPaymentStep] Setting billing interval to:', billingInterval);
        await updateBillingInterval({
          changeBillingIntervalRequest: { billing_interval: billingInterval },
        }).unwrap();

        // Third: Save payment method (activates the subscription)
        console.log('[ConfirmPaymentStep] Saving payment method to backend');
        await updatePaymentMethod({
          updatePaymentMethodRequest: {
            payment_method_id: confirmedPaymentMethodId,
          },
        }).unwrap();

        // Success! Both operations completed
        const paymentDuration = Math.round((Date.now() - paymentStartTime.current) / 1000);

        // Track payment succeeded
        subscription.paymentSucceeded({
          tier_id: selectedTier?.bundle_id,
          tier_name: selectedTier?.name,
          addon_count: selectedAddOns.length,
          addon_ids: selectedAddOns.map(addon => addon.bundle_id),
          addon_names: selectedAddOns.map(addon => addon.name),
          billing_interval: billingInterval,
          total_amount: calculateTotalAmount(),
          payment_duration: paymentDuration,
        });

        // Track subscription created
        subscription.subscriptionCreated({
          tier_id: selectedTier?.bundle_id,
          tier_name: selectedTier?.name,
          addon_count: selectedAddOns.length,
          addon_ids: selectedAddOns.map(addon => addon.bundle_id),
          addon_names: selectedAddOns.map(addon => addon.name),
          billing_interval: billingInterval,
          total_amount: calculateTotalAmount(),
        });

        // Track flow completed
        onFlowCompleted?.();

        setCheckoutState('success');

        // Refetch subscription details to get updated bundles
        await refetchSubscriptionDetails();
      } catch (err: any) {
        console.error('[ConfirmPaymentStep] Activation failed:', err);
        const errorMessage =
          err?.data?.message || 'Failed to activate subscription. Please try again.';
        setError(errorMessage);
        setCheckoutState('error');

        // Track payment failure
        subscription.paymentFailed({
          error_message: errorMessage,
          tier_id: selectedTier?.bundle_id,
          addon_count: selectedAddOns.length,
          total_amount: calculateTotalAmount(),
        });
      }
    },
    [
      assignBundlesToUser,
      updateBillingInterval,
      updatePaymentMethod,
      selectedTier,
      selectedAddOns,
      billingInterval,
      calculateTotalAmount,
      subscription,
      refetchSubscriptionDetails,
      onFlowCompleted,
    ],
  );

  // Handle payment errors from PaymentMethodSection
  const handlePaymentError = useCallback((errorMessage: string) => {
    console.error('[ConfirmPaymentStep] Payment error received from child:', errorMessage);
    setError(errorMessage);
    setCheckoutState('collecting_payment'); // Reset to allow retry
  }, []);

  // Handle when PaymentElement iframe finishes loading
  const handlePaymentElementLoaded = useCallback(() => {
    console.log('[ConfirmPaymentStep] PaymentElement iframe loaded');
    setIsPaymentElementLoaded(true);
  }, []);

  // Handle payment form ready state changes (element loaded AND form complete)
  const handlePaymentFormReadyChange = useCallback((isReady: boolean) => {
    console.log('[ConfirmPaymentStep] Payment form ready state changed:', isReady);
    setIsPaymentFormReady(isReady);
  }, []);

  // Handle activate button click
  const handleActivate = async () => {
    console.log('[ConfirmPaymentStep] Activate button clicked');

    // Track payment submit click
    subscription.paymentSubmitClicked({
      tier_id: selectedTier?.bundle_id,
      tier_name: selectedTier?.name,
      addon_count: selectedAddOns.length,
      addon_ids: selectedAddOns.map(addon => addon.bundle_id),
      addon_names: selectedAddOns.map(addon => addon.name),
      billing_interval: billingInterval,
      total_amount: calculateTotalAmount(),
    });

    paymentStartTime.current = Date.now();

    // If payment method is needed, trigger Stripe confirmation first
    if (needsPaymentMethod && setupIntentSecret) {
      console.log('[ConfirmPaymentStep] Payment method needed, starting Stripe confirmation');
      // Don't change checkoutState - keep PaymentMethodSection mounted
      // Set local processing flag to disable button
      setIsConfirmingPayment(true);
      setError(null);

      try {
        await confirmPaymentMethod();
        console.log('[ConfirmPaymentStep] confirmPaymentMethod completed successfully');
        // Payment confirmation will trigger handlePaymentMethodConfirmed callback
        // The callback will set state to 'processing_bundles'
      } catch (err: any) {
        console.error('[ConfirmPaymentStep] Payment confirmation failed:', err);
        const errorMsg =
          err?.message ||
          'Failed to confirm payment method. Please check your payment details and try again.';
        setError(errorMsg);
        setIsConfirmingPayment(false);

        // Track payment failure
        subscription.paymentFailed({
          error_message: errorMsg,
          tier_id: selectedTier?.bundle_id,
          addon_count: selectedAddOns.length,
          total_amount: calculateTotalAmount(),
        });
        // Stay in collecting_payment state to show error and allow retry
      }
    } else {
      // Payment method already exists, proceed directly to bundle assignment
      console.log('[ConfirmPaymentStep] Payment method exists, proceeding to bundle assignment');
      setCheckoutState('processing_bundles');
      await assignBundlesToUser();
    }
  };

  // Handle retry from error state
  const handleRetry = () => {
    setError(null);
    if (needsPaymentMethod && setupIntentSecret) {
      setCheckoutState('collecting_payment');
    } else {
      setCheckoutState('ready');
    }
  };

  // Render loading state - checking payment
  if (checkoutState === 'checking_payment') {
    return (
      <Box
        sx={{
          width: '100%',
          maxWidth: '1200px',
          mx: 'auto',
          px: { xs: 2, sm: 3 },
          py: 6,
          textAlign: 'center',
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>
          Checking payment information...
        </Typography>
      </Box>
    );
  }

  // Render processing states
  if (checkoutState === 'processing_payment' || checkoutState === 'processing_bundles') {
    return (
      <Box
        sx={{
          width: '100%',
          maxWidth: '1200px',
          mx: 'auto',
          px: { xs: 2, sm: 3 },
          py: 6,
          textAlign: 'center',
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>
          {checkoutState === 'processing_payment'
            ? 'Processing payment method...'
            : 'Activating your subscription...'}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          Please do not close this page.
        </Typography>
      </Box>
    );
  }

  // Render success state
  if (checkoutState === 'success') {
    return (
      <Box
        sx={{
          width: '100%',
          maxWidth: '1200px',
          mx: 'auto',
          px: { xs: 2, sm: 3 },
          py: 6,
          textAlign: 'center',
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
          Subscription Activated!
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Your subscription has been successfully set up. You now have full access to JotPsych.
        </Typography>
      </Box>
    );
  }

  // Render main confirmation screen (ready/collecting_payment/error states)
  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '1200px',
        mx: 'auto',
        px: { xs: 2, sm: 3 },
      }}
    >
      {/* Error Alert */}
      {error && (checkoutState === 'error' || checkoutState === 'collecting_payment') && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          {checkoutState === 'error' && (
            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" size="small" onClick={handleRetry}>
                Try Again
              </Button>
            </Box>
          )}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* Left side: Payment method */}
        <Box sx={{ flex: 1 }}>
          {/* Inline Payment Method Section */}
          {needsPaymentMethod && setupIntentSecret && (
            <PaymentMethodSection
              clientSecret={setupIntentSecret}
              onPaymentMethodConfirmed={handlePaymentMethodConfirmed}
              onError={handlePaymentError}
              onElementLoaded={handlePaymentElementLoaded}
              onReadyChange={handlePaymentFormReadyChange}
              existingBillingDetails={billingInfo}
              isParentProcessing={isConfirmingPayment}
            />
          )}

          {/* Payment method already exists - show card with edit button */}
          {!needsPaymentMethod && existingPaymentMethod && (
            <PaymentMethodCard
              paymentMethod={existingPaymentMethod}
              isLoading={isCheckingPayment}
              onUpdate={() => setShowPaymentModal(true)}
              elevation={1}
              sx={{
                border: '2px solid',
                borderColor: 'divider',
                borderRadius: '12px',
              }}
            />
          )}

          {/* Activate Button */}
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            onClick={handleActivate}
            disabled={
              !selectedTier ||
              isConfirmingPayment ||
              Boolean(needsPaymentMethod && setupIntentSecret && !isPaymentFormReady)
            }
            sx={{
              mt: 3,
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            {isConfirmingPayment
              ? 'Confirming payment...'
              : needsPaymentMethod && setupIntentSecret && !isPaymentElementLoaded
                ? 'Loading payment form...'
                : 'Activate Subscription'}
          </Button>

          {/* Terms */}
          <Typography
            variant="caption"
            sx={{ display: 'block', textAlign: 'center', mt: 2, color: 'text.secondary' }}
          >
            By confirming, you agree to our Terms of Service and acknowledge our Privacy Policy
          </Typography>
        </Box>

        {/* Right side: Plan Summary (appears first on mobile) */}
        <Box sx={{ width: { xs: '100%', md: '400px' }, order: { xs: -1, md: 0 } }}>
          <PlanSummary
            selectedTier={selectedTier}
            selectedAddOns={selectedAddOns}
            billingInterval={billingInterval}
            onBillingIntervalChange={onBillingIntervalChange}
            onChangeTier={onChangeTier}
            showContinueButton={false}
            discount={subscriptionDetails?.discount}
          />
        </Box>
      </Box>

      {/* Modal for updating existing payment method */}
      <UpdatePaymentMethodModal
        open={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSuccess={() => setShowPaymentModal(false)}
      />
    </Box>
  );
};
